/**
 * @param {{resumed?: boolean}} [stateChange]
 * @returns {boolean}
 */
export function shouldResyncOnContinuityLoss(stateChange) {
    return stateChange?.resumed !== true;
}

/**
 * 分頁回到前景才重抓；切到背景時不必動作。
 * @param {string} [visibilityState]
 * @returns {boolean}
 */
export function shouldRefetchOnVisibility(visibilityState) {
    return visibilityState === 'visible';
}

/**
 * 只有 bfcache 還原（上一頁／下一頁）需要重抓——正常載入的頁面本來就是新的。
 * @param {{persisted?: boolean}} [event]
 * @returns {boolean}
 */
export function shouldRefetchOnPageShow(event) {
    return event?.persisted === true;
}

/**
 * 合併同時觸發、單一 in-flight、pending 補跑。
 *
 * 另提供週期心跳：`startPeriodic(ms)` 每隔 ms 觸發一次重抓，走的是同一個 `trigger()`，
 * 因此自動吃到 in-flight 合流，不會與生命週期重抓疊成並行請求。
 * `schedule` / `cancel` 為選用的計時器注入，預設 `setTimeout` / `clearTimeout`。
 *
 * @param {{
 *   run: function(): Promise<void>,
 *   schedule?: function(function(): void, number): *,
 *   cancel?: function(*): void,
 * }} deps
 * @returns {{
 *   trigger: function(string=): void,
 *   startPeriodic: function(number): void,
 *   stopPeriodic: function(): void,
 * }}
 */
export function createRefetchScheduler({ run, schedule, cancel }) {
    const scheduleTimer = schedule || ((fn, delayMs) => setTimeout(fn, delayMs));
    const cancelTimer = cancel || ((id) => clearTimeout(id));

    let inFlight = false;
    let pending = false;
    let pendingReason;
    let activeReason;
    let periodicId;
    let periodicMs;

    function start() {
        inFlight = true;
        let result;
        try {
            result = run();
        } catch (err) {
            result = Promise.reject(err);
        }
        Promise.resolve(result)
            .catch((err) => {
                console.error('[wtoy] refetch failed (reason: ' + activeReason + ')', err);
            })
            .finally(() => {
                inFlight = false;
                if (pending) {
                    pending = false;
                    activeReason = pendingReason;
                    start();
                }
            });
    }

    function trigger(reason) {
        if (inFlight) {
            pending = true;
            pendingReason = reason;
            return;
        }
        activeReason = reason;
        start();
    }

    function armPeriodic() {
        if (periodicMs === undefined) {
            return;
        }
        periodicId = scheduleTimer(() => {
            periodicId = undefined;
            trigger('heartbeat');
            armPeriodic();
        }, periodicMs);
    }

    function stopPeriodic() {
        if (periodicId !== undefined) {
            cancelTimer(periodicId);
            periodicId = undefined;
        }
        periodicMs = undefined;
    }

    function startPeriodic(intervalMs) {
        if (periodicId !== undefined && periodicMs === intervalMs) {
            return;
        }
        stopPeriodic();
        periodicMs = intervalMs;
        armPeriodic();
    }

    return { trigger, startPeriodic, stopPeriodic };
}
